// IMAGES (same subject photos as the Classroom Pilot page)
import SubjectCivics from "Assets/Images/ClassroomPilot/subject-civics.webp";
import SubjectForeignLanguage from "Assets/Images/ClassroomPilot/subject-foreign-language.webp";
import SubjectEnglish from "Assets/Images/ClassroomPilot/subject-english.webp";
import SubjectEconomics from "Assets/Images/ClassroomPilot/subject-economics.webp";
import SubjectHistory from "Assets/Images/ClassroomPilot/subject-history.webp";
import SubjectMath from "Assets/Images/ClassroomPilot/subject-math.webp";
import SubjectScience from "Assets/Images/ClassroomPilot/subject-science.webp";
import { URL_CLASSROOM_PILOT, URL_CONTACT_US, URL_SUBJECTS_DASHBOARD, URL_SUBSCRIPTION } from "Helpers/Paths";

/*
 * Shared content for the Home and About pages. Everything that appears on both pages comes from
 * here, so names, wording, order and colours can never drift apart between the two.
 */

/** Calls to action: the same labels and destinations everywhere. */
export const CTA = {
	pilot: { label: "Join the Classroom Pilot", to: URL_CLASSROOM_PILOT },
	demo: { label: "Request a Demo", to: URL_CONTACT_US },
	subjects: { label: "Explore All Subjects", to: URL_SUBJECTS_DASHBOARD },
	plans: { label: "View Plans & Pricing", to: URL_SUBSCRIPTION },
	testimonials: { label: "Read More Teacher Experiences", to: "/testimonials" },
};

/**
 * The three product pillars. `short` is used on Home (hero, Classroom Pilot band), `text` on About
 * (What We Believe). Same names, icons and colours on both pages.
 */
export const PILLARS = [
	{
		key: "control",
		tone: "blue",
		title: "Teacher-Controlled Content",
		short: "You create and control the material.",
		text: "Teachers decide what their students need to learn. Subjects Master gives you the tools to turn that content into an interactive experience.",
	},
	{
		key: "engagement",
		tone: "green",
		title: "Interactive Student Engagement",
		short: "Turn study guides into friendly competition.",
		text: "Review doesn’t have to feel repetitive. Subjects Master turns your instructional content into interactive gaming study guides.",
	},
	{
		key: "insights",
		tone: "purple",
		title: "Performance Insights",
		short: "See progress and identify areas for reinforcement.",
		text: "Engagement has a purpose. You see what students understand and where additional reinforcement may be needed.",
	},
];

/** How Subjects Master works, in four steps (Home). */
export const STEPS = [
	{ key: "create", tone: "blue", title: "Create", text: "Type your own questions, generate them with AI, or upload your materials." },
	{ key: "engage", tone: "green", title: "Engage", text: "Turn your content into interactive gaming challenges that keep students involved." },
	{ key: "track", tone: "purple", title: "Track", text: "See student progress, attempts, correct and missed questions, and areas that may need reinforcement." },
	{ key: "reuse", tone: "orange", title: "Reuse", text: "Save assessments, reuse questions, and access shared question banks to save time." },
];

/**
 * The seven subjects, in one order on every page. `id` is the anchor on the Subjects page
 * (/subjects#civics), the same ids the footer uses. `tone` tints the bottom of the photo tile.
 */
export const SITE_SUBJECTS = [
	{ id: "math", title: "Math", image: SubjectMath, tone: "#1f63e0" },
	{ id: "science", title: "Science", image: SubjectScience, tone: "#16a34a" },
	{ id: "civics", title: "Civics", image: SubjectCivics, tone: "#d42f32" },
	{ id: "english", title: "English", image: SubjectEnglish, tone: "#7a4ee6" },
	{ id: "history", title: "History", image: SubjectHistory, tone: "#9a5a1c" },
	{ id: "economics", title: "Economics", image: SubjectEconomics, tone: "#e0590f" },
	{ id: "foreign-language", title: "Foreign Language", image: SubjectForeignLanguage, tone: "#0e7c86" },
];

/** Featured teacher testimonial (Home and About). Full text is on the Testimonials page. */
export const FEATURED_TESTIMONIAL = {
	quote: "My seniors really love Subjects Master! Because I can customize the Econ and Government questions, the students share how helpful it is to review before quizzes.",
	name: "Heather Lehigh",
	role: "Department Chair, Social Sciences",
	school: "EHS",
};

/** Closing band on the Subjects and Testimonials pages (the wording of the old "Request Your Free Demo Today" band). */
export const DEMO_BAND = {
	title: "Request Your Free Demo Today",
	text: "Ready to experience the power of game-based learning? Request a live demo and see how easy it is to create custom study guides, engage students across subjects, and track learning in real time.",
};

/** Plans (Home). Each card opens the Subscription page with that plan selected. */
export const PLANS = [
	{ key: "basic", tone: "blue", title: "Basic", text: "For individual educators and families" },
	{ key: "premium", tone: "green", title: "Premium", text: "For educators, homeschool networks and small schools" },
	{ key: "professional", tone: "purple", title: "Professional", text: "For schools and organizations that need collaboration tools" },
];
